import { cn } from "@/lib/utils";
import * as React from "react";

export interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Band fill. kuebefarm.lu alternates white and cream down the page — that
   * alternation, not borders, is what separates one section from the next.
   */
  tone?: "white" | "cream" | "cream-light" | "ink" | "transparent";
  /** Vertical rhythm. `lg` is the site's 132px band. */
  size?: "none" | "sm" | "md" | "lg";
  /** Skip the centred container — for full-bleed photography. */
  bleed?: boolean;
  /** Hairline above the section. Use sparingly. */
  ruleTop?: boolean;
  as?: "section" | "div" | "header" | "footer" | "article";
}

const paddingBySize: Record<NonNullable<SectionProps["size"]>, string> = {
  none: "",
  sm: "py-14 md:py-20",
  md: "py-20 md:py-28",
  // The 132px rhythm, eased down on small screens so it does not become a
  // scroll of empty space on a phone.
  lg: "py-24 md:py-32 lg:py-band",
};

const bgByTone: Record<NonNullable<SectionProps["tone"]>, string> = {
  white: "bg-kf-white",
  cream: "bg-kf-cream",
  "cream-light": "bg-kf-cream-light",
  ink: "bg-kf-ink text-kf-white",
  transparent: "",
};

/**
 * One horizontal band of the page. Everything on the site is a stack of these.
 */
const Section = React.forwardRef<HTMLElement, SectionProps>(
  (
    {
      className,
      children,
      tone = "white",
      size = "lg",
      bleed = false,
      ruleTop = false,
      as: Tag = "section",
      ...props
    },
    ref
  ) => (
    <Tag
      // `as` swaps the element type at runtime; the ref is always an element.
      ref={ref as React.Ref<HTMLDivElement & HTMLElement>}
      className={cn(
        "relative w-full",
        bgByTone[tone],
        paddingBySize[size],
        ruleTop && "border-t border-kf-line",
        className
      )}
      {...props}
    >
      {bleed ? children : <div className="container">{children}</div>}
    </Tag>
  )
);
Section.displayName = "Section";

export { Section };
